import { useState, useEffect } from "react";
import axios from "axios";
import uploadToDropbox from "../../services/dropboxService";
import {
  PageHeader,
  Panel,
  Modal,
  ConfirmDialog,
  Toolbar,
  ToolbarField,
  StatCardRow,
} from "../admin";
import DataTable from "../admin/DataTable";
import useRowActionFeedback from "../admin/useRowActionFeedback";

function Award() {
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    imageUrl: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const { busyId, flashId, flashKind, run } = useRowActionFeedback();

  const fetchAwards = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/awards`);
      setAwards(response.data);
    } catch (error) {
      console.error("Error fetching awards:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAwards();
  }, []);

  const handleInputChange = async (e) => {
    if (e.target.type === "file") {
      try {
        setIsUploading(true);
        const file = e.target.files[0];
        const downloadUrl = await uploadToDropbox(file);
        setFormData((prev) => ({ ...prev, imageUrl: downloadUrl }));
      } catch (error) {
        console.error("Error uploading image:", error);
      } finally {
        setIsUploading(false);
      }
    } else {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value,
      });
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    run({
      id: editingId || "new",
      action: async () => {
        if (editingId) {
          await axios.put(
            `${process.env.REACT_APP_API_URL}/awards/${editingId}`,
            formData
          );
        } else {
          await axios.post(`${process.env.REACT_APP_API_URL}/awards`, formData);
        }
        setIsModalOpen(false);
        setEditingId(null);
      },
      success: editingId ? "Award updated" : "Award created",
      error: "Could not save award",
      flash: "success",
      refresh: fetchAwards,
    }).catch(() => {});
  };

  const handleDelete = () =>
    run({
      id: deleting.awardId,
      action: async () => {
        await axios.delete(`${process.env.REACT_APP_API_URL}/awards/${deleting.awardId}`);
        setAwards((prev) => prev.filter((a) => a.awardId !== deleting.awardId));
        setDeleting(null);
      },
      success: "Award deleted",
      error: "Could not delete award",
      flash: "danger",
    }).catch(() => {});

  const handleEdit = (award) => {
    setFormData({
      name: award.name,
      description: award.description,
      category: award.category,
      imageUrl: award.imageUrl,
    });
    setEditingId(award.awardId);
    setIsModalOpen(true);
  };

  const handleNewAward = () => {
    setFormData({ name: "", description: "", category: "", imageUrl: "" });
    setEditingId(null);
    setIsModalOpen(true);
  };

  const Spinner = () => (
    <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-line-strong border-t-accent" />
  );

  /* Derived data */
  const categories = [...new Set(awards.map((a) => a.category).filter(Boolean))];
  const filtered = awards.filter((a) => {
    const matchesSearch =
      !search ||
      a.name?.toLowerCase().includes(search.toLowerCase()) ||
      a.description?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = categoryFilter === "all" || a.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Awards"
        description="Manage award definitions available for nominations"
        actions={
          <button type="button" className="btn-primary" onClick={handleNewAward}>
            Add new award
          </button>
        }
      />

      <StatCardRow
        stats={[
          { label: "Total awards", value: awards.length },
          { label: "Categories", value: categories.length },
          {
            label: "With cover image",
            value: awards.filter((a) => a.imageUrl).length,
          },
        ]}
      />

      <Toolbar>
        <ToolbarField label="Search">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or description…"
            className="field"
          />
        </ToolbarField>
        <ToolbarField label="Category" className="max-w-[220px]">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="field"
          >
            <option value="all">All categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </ToolbarField>
        <button
          type="button"
          className="btn-ghost"
          onClick={() => {
            setSearch("");
            setCategoryFilter("all");
          }}
        >
          Clear
        </button>
      </Toolbar>

      <DataTable
        cols={[
          {
            key: "imageUrl",
            label: "",
            render: (award) =>
              award.imageUrl ? (
                <img
                  src={award.imageUrl}
                  alt={award.name}
                  className="h-9 w-9 object-cover rounded-lg border border-line-strong"
                />
              ) : (
                <div className="h-9 w-9 rounded-lg bg-surface-700 border border-line-strong flex items-center justify-center text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.515M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0" />
                  </svg>
                </div>
              ),
          },
          {
            key: "name",
            label: "Award",
            render: (award) => (
              <div>
                <p className="text-white font-medium leading-tight">{award.name}</p>
                <p className="text-xs text-gray-500 truncate max-w-md">{award.description}</p>
              </div>
            ),
          },
          {
            key: "category",
            label: "Category",
            render: (award) =>
              award.category ? (
                <span className="badge-neutral">{award.category}</span>
              ) : (
                <span className="text-gray-600">—</span>
              ),
          },
        ]}
        rows={filtered}
        keyField="awardId"
        loading={loading}
        busyId={busyId}
        busyField="awardId"
        flashId={flashId}
        flashKind={flashKind}
        emptyTitle="No awards found"
        emptyMessage={
          search || categoryFilter !== "all"
            ? "No awards match your filters."
            : "Create your first award to make it available for nominations."
        }
        emptyAction={
          !search && categoryFilter === "all" ? (
            <button type="button" className="btn-primary" onClick={handleNewAward}>
              Add new award
            </button>
          ) : null
        }
        rowActions={(award) => (
          <>
            <button type="button" className="btn-ghost !py-1.5 !px-3" onClick={() => handleEdit(award)}>
              Edit
            </button>
            <button type="button" className="btn-danger !py-1.5 !px-3" onClick={() => setDeleting(award)}>
              Delete
            </button>
          </>
        )}
      />

      {/* Modal Form */}
      {isModalOpen && (
        <Modal
          title={editingId ? "Edit award" : "Add new award"}
          onClose={() => setIsModalOpen(false)}
        >
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="field-label">Award image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleInputChange}
                className="field !py-2.5 file:mr-3 file:rounded-md file:border-0 file:bg-surface-600 file:px-3 file:py-1.5 file:text-white hover:file:bg-surface-700"
                disabled={isUploading}
              />
              {isUploading && (
                <div className="mt-2 flex items-center text-gray-400 text-sm">
                  <Spinner />
                  <span className="ml-2">Uploading image…</span>
                </div>
              )}
              {formData.imageUrl && !isUploading && (
                <img
                  src={formData.imageUrl}
                  alt="Preview"
                  className="mt-2 h-24 w-24 object-cover rounded-lg border border-line-strong"
                />
              )}
            </div>
            <div>
              <label className="field-label">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Award name"
                className="field"
                required
              />
            </div>
            <div>
              <label className="field-label">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Description"
                className="field min-h-[80px]"
              />
            </div>
            <div>
              <label className="field-label">Category</label>
              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                placeholder="Category"
                className="field"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSubmitting || isUploading}
              >
                {isSubmitting ? (
                  <>
                    <Spinner />
                    <span className="ml-2">{editingId ? "Updating…" : "Creating…"}</span>
                  </>
                ) : editingId ? (
                  "Update award"
                ) : (
                  "Create award"
                )}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {deleting && (
        <ConfirmDialog
          title="Delete award"
          message={`Delete "${deleting.name}"? This cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleting(null)}
          busy={busyId === deleting.awardId}
        />
      )}
    </div>
  );
}

export default Award;
